import { useState, type JSX } from "react";
import { useAnalytics } from "../../../analytics/provider";
import { useBillingLimit } from "../hooks/useBillingLimit";
import { ErrorNotice } from "./ErrorNotice";
import { fmtCost } from "./format";

/**
 * Credit-limit card (plan billing-hardening Stage 6): shows the caller's
 * limit / lifetime spend / entitlement and edits the limit via the
 * billing.limits.* RPCs. Enforcement lives server-side (the turn gate);
 * this card is just its settings surface.
 */
export function LimitCard(): JSX.Element {
  const analytics = useAnalytics();
  const isHostedDemo = analytics.runtimeSurface === "hosted_demo";
  const { limit, loading, error, saving, saveError, setLimit, reload } = useBillingLimit();
  const [draft, setDraft] = useState("");
  const [draftError, setDraftError] = useState<string | null>(null);

  if (loading) {
    return <div className="p-4 text-sm text-content-tertiary">Loading...</div>;
  }
  if (error) {
    return (
      <div className="p-4">
        <ErrorNotice message={error} onRetry={reload} />
      </div>
    );
  }
  if (!limit) {
    return <div className="p-4 text-sm text-content-tertiary">Unavailable</div>;
  }

  const hasLimit = limit.credit_limit_micros !== null;

  const handleSave = (): void => {
    if (isHostedDemo) return;
    const usd = Number(draft.trim().replace(",", "."));
    if (!/^[0-9]*[.,]?[0-9]+$/.test(draft.trim()) || !Number.isFinite(usd) || usd < 0) {
      setDraftError("Enter a non-negative USD amount, e.g. 5 or 12.50");
      return;
    }
    setDraftError(null);
    setLimit(Math.round(usd * 1_000_000));
    setDraft("");
  };
  const handleClear = (): void => {
    if (isHostedDemo) return;
    setLimit(null);
  };

  return (
    <div className="p-4 flex flex-col gap-3">
      <div className="flex items-center gap-6">
        <div>
          <div className="text-xs text-content-secondary">Credit limit</div>
          <div className="text-sm text-content">
            {limit.credit_limit_micros !== null
              ? fmtCost(limit.credit_limit_micros)
              : "No limit configured"}
          </div>
        </div>
        <div>
          <div className="text-xs text-content-secondary">Lifetime spend</div>
          <div className="text-sm text-content">{fmtCost(limit.spent_micros)}</div>
        </div>
        <div>
          <div className="text-xs text-content-secondary">Status</div>
          {limit.entitled ? (
            <div className="text-sm text-content">Active</div>
          ) : (
            <div className="text-sm text-red-400">Credit exhausted</div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          disabled={isHostedDemo}
          title={isHostedDemo ? "Managed by Hosted Demo" : undefined}
          value={draft}
          onChange={(e): void => {
            setDraft(e.target.value);
          }}
          placeholder="Limit in USD"
          inputMode="decimal"
          className="rounded-lg border border-edge bg-surface-tertiary px-3 py-2 text-sm text-content outline-none focus:border-content-muted"
        />
        <button
          type="button"
          onClick={handleSave}
          disabled={isHostedDemo || saving || draft.trim() === ""}
          title={isHostedDemo ? "Managed by Hosted Demo" : undefined}
          className="text-xs px-3 py-1.5 rounded bg-accent text-white disabled:opacity-50 border-none cursor-pointer"
        >
          Save limit
        </button>
        {hasLimit && (
          <button
            type="button"
            onClick={handleClear}
            disabled={isHostedDemo || saving}
            title={isHostedDemo ? "Managed by Hosted Demo" : undefined}
            className="text-xs px-3 py-1.5 rounded border border-edge text-content-secondary enabled:hover:text-content disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Clear limit
          </button>
        )}
      </div>
      {isHostedDemo && (
        <p className="text-xs text-content-tertiary m-0">
          Managed by Hosted Demo. This shared demo credit limit cannot be
          changed.
        </p>
      )}
      {draftError && <p className="text-xs text-red-400 m-0">{draftError}</p>}
      {!isHostedDemo && saveError && (
        <p className="text-xs text-red-400 m-0">{saveError}</p>
      )}
    </div>
  );
}
